'use client';

import React, { useState, useRef, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import { trackEvent } from '@/components/GoogleAnalytics';
import { useToast } from '@/context/ToastContext';

interface TeklifModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TeklifForm {
  adSoyad: string;
  eposta: string;
  projeTuru: string;
  teknolojiTercihleri: string;
  projeAciklamasi: string;
  butceAraligi: string;
  zamanCizelgesi: string;
}

const PROJE_TURLERI = ['Proje Ortaklığı', 'Proje Kodlama', 'Danışmanlık', 'Diğer'];

const BUTCE_ARALIK = [
  '< 5.000 ₺',
  '5.000 – 15.000 ₺',
  '15.000 – 30.000 ₺',
  '30.000 – 60.000 ₺',
  '60.000 ₺+',
  'Belirtmek istemiyorum',
];

const ZAMAN_CIZELGESI = ['1 haftadan az', '1–4 hafta', '1–3 ay', '3–6 ay', '6 ay+', 'Esnek'];

export default function TeklifModal({ isOpen, onClose }: TeklifModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const { showToast } = useToast();
  const [form, setForm] = useState<TeklifForm>({
    adSoyad: '',
    eposta: '',
    projeTuru: '',
    teknolojiTercihleri: '',
    projeAciklamasi: '',
    butceAraligi: '',
    zamanCizelgesi: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referenceNumber, setReferenceNumber] = useState<string>('');

  const getErrorMessage = (err: unknown, fallback: string) => {
    if (err instanceof Error && err.message) return err.message;
    if (typeof err === 'string') return err;
    if (err && typeof err === 'object') {
      const message = (err as { message?: unknown }).message;
      if (typeof message === 'string' && message) return message;
    }
    return fallback;
  };

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    // Track modal open
    trackEvent('proposal_form_open', { event_category: 'engagement' });
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSubmitted(false);
      setError(null);
      setForm({
        adSoyad: '',
        eposta: '',
        projeTuru: '',
        teknolojiTercihleri: '',
        projeAciklamasi: '',
        butceAraligi: '',
        zamanCizelgesi: '',
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleChange = (field: keyof TeklifForm, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.adSoyad || !form.eposta || !form.projeTuru) {
      setError('Lütfen zorunlu alanları doldurun.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const supabase = createClient();
      const { error: insertError } = await supabase.from('teklifler').insert({
        ad_soyad: form.adSoyad,
        eposta: form.eposta,
        proje_turu: form.projeTuru,
        teknoloji_tercihleri: form.teknolojiTercihleri || null,
        proje_aciklamasi: form.projeAciklamasi || null,
        butce_araligi: form.butceAraligi || null,
        zaman_cizelgesi: form.zamanCizelgesi || null,
      });
      if (insertError) {
        setError(insertError.message || 'Gönderim sırasında bir hata oluştu.');
        return;
      }

      // Generate unique reference number
      const refNum = `TKL-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
      setReferenceNumber(refNum);

      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      if (supabaseUrl) {
        // Fire-and-forget: send admin alert email
        fetch(`${supabaseUrl}/functions/v1/send-proposal-alert`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adSoyad: form.adSoyad,
            eposta: form.eposta,
            projeTuru: form.projeTuru,
            butceAraligi: form.butceAraligi,
            projeAciklamasi: form.projeAciklamasi,
            teknolojiTercihleri: form.teknolojiTercihleri,
            zamanCizelgesi: form.zamanCizelgesi,
          }),
        }).catch(() => {});

        // Fire-and-forget: send confirmation email to proposer
        fetch(`${supabaseUrl}/functions/v1/send-proposal-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            adSoyad: form.adSoyad,
            eposta: form.eposta,
            projeTuru: form.projeTuru,
            butceAraligi: form.butceAraligi,
            zamanCizelgesi: form.zamanCizelgesi,
            referenceNumber: refNum,
          }),
        }).catch(() => {});
      }

      setSubmitted(true);
      // Show toast notification for successful submission
      showToast(
        'Teklifiniz başarıyla gönderildi! En kısa sürede sizinle iletişime geçeceğiz.',
        'success',
        5000
      );
      // Track successful proposal submission
      trackEvent('proposal_submitted', {
        event_category: 'engagement',
        project_type: form.projeTuru,
        budget_range: form.butceAraligi || 'not_specified',
        timeline: form.zamanCizelgesi || 'not_specified',
      });
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Beklenmeyen bir hata oluştu.'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(200,206,214,0.12)',
    color: '#EDF2F7',
    outline: 'none',
  };

  const selectStyle = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(99,179,237,0.25)',
    color: '#EDF2F7',
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none' as const,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2363b3ed' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: '36px',
  };

  const labelStyle = {
    color: 'rgba(237,242,247,0.5)',
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }}
    >
      <div
        className="relative w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col"
        style={{
          background: '#141c28',
          border: '1px solid rgba(167,139,250,0.2)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.5)',
          maxHeight: '92vh',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(200,206,214,0.08)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(167,139,250,0.1)',
                border: '1px solid rgba(167,139,250,0.25)',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 2h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z"
                  stroke="#a78bfa"
                  strokeWidth="1.3"
                />
                <path
                  d="M5 5h6M5 7.5h6M5 10h4"
                  stroke="#a78bfa"
                  strokeWidth="1.3"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div>
              <h2
                className="font-display font-bold text-sm sm:text-base"
                style={{ color: '#EDF2F7' }}
              >
                Teklif Al
              </h2>
              <p className="text-xs" style={{ color: 'rgba(237,242,247,0.45)' }}>
                Proje detaylarınızı paylaşın
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center transition-all active:scale-95 hover:scale-110"
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(200,206,214,0.1)',
              color: 'rgba(237,242,247,0.5)',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 2l10 10M12 2L2 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4 sm:py-5">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-8 sm:py-12 text-center">
              <div
                className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mb-4"
                style={{
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.3)',
                }}
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path
                    d="M6 14l6 6 10-10"
                    stroke="#4ade80"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <h3
                className="font-display font-bold text-lg sm:text-xl mb-2"
                style={{ color: '#EDF2F7' }}
              >
                Teklifiniz Alındı!
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(237,242,247,0.55)' }}>
                Proje teklifiniz başarıyla iletildi. En kısa sürede sizinle iletişime geçeceğim.
              </p>
              {referenceNumber && (
                <div
                  className="mt-4 px-4 sm:px-5 py-3 rounded-xl w-full"
                  style={{
                    background: 'rgba(167,139,250,0.08)',
                    border: '1px solid rgba(167,139,250,0.25)',
                  }}
                >
                  <p
                    className="text-xs mb-1"
                    style={{
                      color: 'rgba(237,242,247,0.45)',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                    }}
                  >
                    Referans Numaranız
                  </p>
                  <p
                    className="font-bold text-base sm:text-lg tracking-widest"
                    style={{ color: '#a78bfa' }}
                  >
                    {referenceNumber}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'rgba(237,242,247,0.4)' }}>
                    Bu numara onay e-postanıza da gönderildi.
                  </p>
                </div>
              )}
              <div
                className="mt-4 px-4 py-3 rounded-xl w-full text-left"
                style={{
                  background: 'rgba(99,179,237,0.07)',
                  border: '1px solid rgba(99,179,237,0.2)',
                }}
              >
                <p className="text-xs font-semibold mb-1" style={{ color: '#63b3ed' }}>
                  ⏱️ Beklenen Yanıt Süresi
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'rgba(237,242,247,0.6)' }}>
                  Genellikle <strong style={{ color: '#EDF2F7' }}>1–3 iş günü</strong> içinde
                  e-posta adresinize geri dönüş yapılmaktadır.
                </p>
              </div>
              <button
                onClick={onClose}
                className="mt-5 sm:mt-6 px-6 py-3 sm:py-2.5 rounded-full font-display font-semibold text-sm text-white active:scale-95"
                style={{ background: 'linear-gradient(135deg, #a78bfa, #63b3ed)' }}
              >
                Kapat
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {/* Ad Soyad */}
              <div>
                <label
                  className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                  style={labelStyle}
                >
                  Ad Soyad <span style={{ color: '#f87171' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.adSoyad}
                  onChange={(e) => handleChange('adSoyad', e.target.value)}
                  placeholder="Adınız Soyadınız"
                  className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm"
                  style={inputStyle}
                  required
                />
              </div>

              {/* E-posta */}
              <div>
                <label
                  className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                  style={labelStyle}
                >
                  E-posta <span style={{ color: '#f87171' }}>*</span>
                </label>
                <input
                  type="email"
                  value={form.eposta}
                  onChange={(e) => handleChange('eposta', e.target.value)}
                  placeholder="ornek@email.com"
                  className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm"
                  style={inputStyle}
                  required
                />
              </div>

              {/* Proje Türü */}
              <div>
                <label
                  className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                  style={labelStyle}
                >
                  Proje Türü <span style={{ color: '#f87171' }}>*</span>
                </label>
                <select
                  value={form.projeTuru}
                  onChange={(e) => handleChange('projeTuru', e.target.value)}
                  className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm transition-all duration-200 hover:border-blue-400/50 focus:border-blue-400/70 focus:ring-1 focus:ring-blue-400/20"
                  style={selectStyle}
                  required
                >
                  <option value="" style={{ background: '#141c28' }}>
                    Seçiniz...
                  </option>
                  {PROJE_TURLERI?.map((tur) => (
                    <option key={tur} value={tur} style={{ background: '#141c28' }}>
                      {tur}
                    </option>
                  ))}
                </select>
              </div>

              {/* Teknoloji Tercihleri */}
              <div>
                <label
                  className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                  style={labelStyle}
                >
                  Teknoloji Tercihleri
                </label>
                <input
                  type="text"
                  value={form.teknolojiTercihleri}
                  onChange={(e) => handleChange('teknolojiTercihleri', e.target.value)}
                  placeholder="React, .NET Core, Angular..."
                  className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm"
                  style={inputStyle}
                />
              </div>

              {/* Proje Açıklaması */}
              <div>
                <label
                  className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                  style={labelStyle}
                >
                  Proje Açıklaması
                </label>
                <textarea
                  value={form.projeAciklamasi}
                  onChange={(e) => handleChange('projeAciklamasi', e.target.value)}
                  placeholder="Projenizi kısaca açıklayın..."
                  rows={3}
                  className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm resize-none"
                  style={inputStyle}
                />
              </div>

              {/* Bütçe & Zaman — stacked on mobile, 2 col on sm+ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label
                    className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                    style={labelStyle}
                  >
                    Bütçe Aralığı
                  </label>
                  <select
                    value={form.butceAraligi}
                    onChange={(e) => handleChange('butceAraligi', e.target.value)}
                    className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm transition-all duration-200 hover:border-blue-400/50 focus:border-blue-400/70 focus:ring-1 focus:ring-blue-400/20"
                    style={selectStyle}
                  >
                    <option value="" style={{ background: '#141c28' }}>
                      Seçiniz...
                    </option>
                    {BUTCE_ARALIK?.map((b) => (
                      <option key={b} value={b} style={{ background: '#141c28' }}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    className="block text-xs font-semibold mb-1.5 uppercase tracking-wider"
                    style={labelStyle}
                  >
                    Zaman Çizelgesi
                  </label>
                  <select
                    value={form.zamanCizelgesi}
                    onChange={(e) => handleChange('zamanCizelgesi', e.target.value)}
                    className="w-full px-4 py-3 sm:py-2.5 rounded-xl text-sm transition-all duration-200 hover:border-blue-400/50 focus:border-blue-400/70 focus:ring-1 focus:ring-blue-400/20"
                    style={selectStyle}
                  >
                    <option value="" style={{ background: '#141c28' }}>
                      Seçiniz...
                    </option>
                    {ZAMAN_CIZELGESI?.map((z) => (
                      <option key={z} value={z} style={{ background: '#141c28' }}>
                        {z}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div
                  className="px-4 py-3 rounded-xl text-sm"
                  style={{
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.3)',
                    color: '#f87171',
                  }}
                >
                  {error}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3.5 sm:py-3 rounded-xl font-display font-bold text-sm text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: submitting
                    ? 'rgba(167,139,250,0.3)'
                    : 'linear-gradient(135deg, #a78bfa, #63b3ed)',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span
                      className="w-4 h-4 rounded-full border-2 border-t-transparent animate-spin"
                      style={{
                        borderColor: 'rgba(255,255,255,0.5)',
                        borderTopColor: 'transparent',
                      }}
                    />
                    Gönderiliyor...
                  </span>
                ) : (
                  'Teklif Gönder'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
